import { createContext, useContext } from 'react';

import MUIRadio, { RadioProps as MUIRadioProps } from '@mui/material/Radio';
import clsx from 'clsx';
import capitalize from 'lodash/capitalize';

import { WithRef } from '../../../types';

import {
  ControlLabelSettings,
  useControlLabelSettings,
} from '../ControlLabelContext';

type CustomProps = {
  testId?: string;
};

type RadioIconProps = { checked?: boolean };
const RadioIcon = ({ checked }: RadioIconProps) => (
  <div className={clsx('RadioIconBase', checked && 'RadioIconBase-checked')} />
);

export type RadioProps = Omit<MUIRadioProps, 'color'> & CustomProps;

const defaults: RadioProps = {
  size: 'small',
};

export const RadioContext = createContext<RadioProps>(defaults);

/**
 * Maps ControlLabelContext settings into Radio props.
 *
 * Merge order: defaults < RadioContext < board < own props.
 * - required: emit `required` + `aria-required` (MUI routes `required` to the
 *   native input, `aria-required` to the root — same DOM as before).
 * - disabled: maps only board `true` (a board `false` would shadow a
 *   RadioContext `true`).
 */
const mapRadioBoard = (
  cl?: ControlLabelSettings
): Partial<RadioProps> & { 'aria-required'?: boolean } => {
  if (!cl) return {};
  const board: Partial<RadioProps> & { 'aria-required'?: boolean } = {};
  // Size mapping mirrors the board broadcast: ControlLabelSize sm/md -> MUI small/medium.
  if (cl.size === 'sm') board.size = 'small';
  if (cl.size === 'md') board.size = 'medium';
  // The label's `for` points at this id — MUI routes `id` to the native input.
  if (cl.id) board.id = cl.id;
  if (cl.disabled === true) board.disabled = true;
  if (cl.required === true) {
    board.required = true;
    board['aria-required'] = true;
  }
  return board;
};

export const Radio = ({
  ref,
  testId,
  ...rest
}: WithRef<HTMLButtonElement, RadioProps>) => {
  const radioContext = useContext(RadioContext);
  const cl = useControlLabelSettings();
  const props = { ...defaults, ...radioContext, ...mapRadioBoard(cl), ...rest };
  return (
    <MUIRadio
      icon={<RadioIcon />}
      checkedIcon={<RadioIcon checked />}
      data-testid={testId}
      {...radioContext}
      {...props}
      color="primary"
      // FormControlLabel adds 'disabled: undefined'
      disabled={
        props.disabled ??
        radioContext.disabled ??
        props.readOnly ??
        radioContext.readOnly
      }
      className={clsx(
        props.className,
        'DsRadio-Root',
        `DsRadio-size${capitalize(props.size)}`
      )}
      ref={ref}
    />
  );
};

Radio.displayName = 'Radio';
